import React, { useEffect, useState } from 'react';
import { SaveIcon, RotateCcwIcon, ShieldIcon, SlidersIcon, BellIcon, DatabaseIcon, CheckCircleIcon, AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { Toggle as ToggleSwitch } from '../../../../components/ui/Toggle';
import { Input } from '../../../../components/ui/Input';
import { loadGovernanceConfig, saveGovernanceConfig, resetGovernanceConfig, getDefaultGovernanceConfig, type GovernanceConfigOverrides } from '../../../../governance';
interface ToggleProps {
  label: string;
  description: string;
  value: boolean;
  onChange: (v: boolean) => void;
}
function Toggle({
  label,
  description,
  value,
  onChange
}: ToggleProps) {
  return <div className="flex items-start justify-between gap-4 py-4 border-b border-gray-50 last:border-0" data-id="element-3247">
      <div className="min-w-0" data-id="element-3248">
        <p className="text-sm font-medium text-gray-800" data-id="element-3249">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5" data-id="element-3250">{description}</p>
      </div>
      <ToggleSwitch checked={value} onChange={onChange} label={label} variant="admin" data-id="element-3251" />
    </div>;
}
interface SliderFieldProps {
  label: string;
  description: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
  unit?: string;
  colorFn?: (v: number) => string;
}
function SliderField({
  label,
  description,
  value,
  min,
  max,
  step = 1,
  onChange,
  unit = '',
  colorFn
}: SliderFieldProps) {
  const pct = (value - min) / (max - min) * 100;
  const color = colorFn ? colorFn(value) : 'text-blue-600';
  return <div className="py-4 border-b border-gray-50 last:border-0" data-id="element-3252">
      <div className="flex items-center justify-between mb-1" data-id="element-3253">
        <p className="text-sm font-medium text-gray-800" data-id="element-3254">{label}</p>
        <span className={`text-sm font-bold ${color}`} data-id="element-3255">
          {value}
          {unit}
        </span>
      </div>
      <p className="text-xs text-gray-500 mb-3" data-id="element-3256">{description}</p>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-200 accent-blue-500" data-id="element-3257" />
      <div className="flex justify-between mt-1" data-id="element-3258">
        <span className="text-xs text-gray-400" data-id="element-3259">
          {min}
          {unit}
        </span>
        <span className="text-xs text-gray-400" data-id="element-3260">
          {max}
          {unit}
        </span>
      </div>
    </div>;
}
interface NumberFieldProps {
  label: string;
  description: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  unit?: string;
}
function NumberField({
  label,
  description,
  value,
  min,
  max,
  onChange,
  unit
}: NumberFieldProps) {
  return <div className="py-4 border-b border-gray-50 last:border-0" data-id="element-3261">
      <div className="flex items-start justify-between gap-4" data-id="element-3262">
        <div className="min-w-0" data-id="element-3263">
          <p className="text-sm font-medium text-gray-800" data-id="element-3264">{label}</p>
          <p className="text-xs text-gray-500 mt-0.5" data-id="element-3265">{description}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3266">
          <Input type="number" value={value} min={min} max={max} onChange={e => onChange(Number(e.target.value))} fullWidth={false} className="w-20 text-right" data-id="element-3267" />
          {unit && <span className="text-sm text-gray-500" data-id="element-3268">{unit}</span>}
        </div>
      </div>
    </div>;
}
type SectionId = 'kwaliteit' | 'pipeline' | 'beleid' | 'meldingen' | 'systeem';
export function GovernanceSettingsTab() {
  const [activeSection, setActiveSection] = useState<SectionId>('kwaliteit');
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const defaults = getDefaultGovernanceConfig();
  const [config, setConfig] = useState<GovernanceConfigOverrides>(defaults);
  const [minCogLevel, setMinCogLevel] = useState(defaults.qualityGates?.minCogLevel ?? 2);
  const [maxCogLevel, setMaxCogLevel] = useState(defaults.qualityGates?.maxCogLevel ?? 5);
  const [minDidacticDepth, setMinDidacticDepth] = useState(defaults.qualityGates?.minDidacticDepth ?? 2);
  const [minQualityScore, setMinQualityScore] = useState(defaults.qualityGates?.minQualityScore ?? 70);
  const [schemaEnabled, setSchemaEnabled] = useState(defaults.pipelineStages?.schemaEnabled ?? true);
  const [typeEnabled, setTypeEnabled] = useState(defaults.pipelineStages?.typeEnabled ?? true);
  const [contentEnabled, setContentEnabled] = useState(defaults.pipelineStages?.contentEnabled ?? true);
  const [supportEnabled, setSupportEnabled] = useState(defaults.pipelineStages?.supportEnabled ?? true);
  const [visualEnabled, setVisualEnabled] = useState(defaults.pipelineStages?.visualEnabled ?? true);
  const [graphEnabled, setGraphEnabled] = useState(defaults.pipelineStages?.graphEnabled ?? true);
  const [strictMode, setStrictMode] = useState(defaults.pipelineBehavior?.strictMode ?? false);
  const [autoBlock, setAutoBlock] = useState(defaults.pipelineBehavior?.autoBlock ?? true);
  const [minQuestionsPerSkill, setMinQuestionsPerSkill] = useState(defaults.policy?.minQuestionsPerSkill ?? 5);
  const [maxQuestionsPerSkill, setMaxQuestionsPerSkill] = useState(defaults.policy?.maxQuestionsPerSkill ?? 50);
  const [requireHints, setRequireHints] = useState(defaults.policy?.requireHints ?? true);
  const [requireExplanation, setRequireExplanation] = useState(defaults.policy?.requireExplanation ?? true);
  const [requireVisuals, setRequireVisuals] = useState(defaults.policy?.requireVisuals ?? false);
  const [distributionCheck, setDistributionCheck] = useState(defaults.policy?.distributionCheck ?? true);
  const [notifyBlockers, setNotifyBlockers] = useState(config.notifications?.notifyBlockers ?? true);
  const [notifyWarnings, setNotifyWarnings] = useState(config.notifications?.notifyWarnings ?? false);
  const [notifyWeeklyReport, setNotifyWeeklyReport] = useState(config.notifications?.notifyWeeklyReport ?? true);
  const [notifyNewRules, setNotifyNewRules] = useState(config.notifications?.notifyNewRules ?? true);
  const [cacheEnabled, setCacheEnabled] = useState(config.system?.cacheEnabled ?? true);
  const [debugMode, setDebugMode] = useState(config.system?.debugMode ?? false);
  const [autoValidateOnSave, setAutoValidateOnSave] = useState(config.system?.autoValidateOnSave ?? true);
  const [batchSize, setBatchSize] = useState(config.system?.batchSize ?? 100);
  const markChanged = () => {
    setHasChanges(true);
    setSaved(false);
  };
  // Load persisted config from dataLayer on mount
  useEffect(() => {
    loadGovernanceConfig().then(loaded => {
      setConfig(loaded);
      setMinCogLevel(loaded.qualityGates?.minCogLevel ?? 2);
      setMaxCogLevel(loaded.qualityGates?.maxCogLevel ?? 5);
      setMinDidacticDepth(loaded.qualityGates?.minDidacticDepth ?? 2);
      setMinQualityScore(loaded.qualityGates?.minQualityScore ?? 70);
      setSchemaEnabled(loaded.pipelineStages?.schemaEnabled ?? true);
      setTypeEnabled(loaded.pipelineStages?.typeEnabled ?? true);
      setContentEnabled(loaded.pipelineStages?.contentEnabled ?? true);
      setSupportEnabled(loaded.pipelineStages?.supportEnabled ?? true);
      setVisualEnabled(loaded.pipelineStages?.visualEnabled ?? true);
      setGraphEnabled(loaded.pipelineStages?.graphEnabled ?? true);
      setStrictMode(loaded.pipelineBehavior?.strictMode ?? false);
      setAutoBlock(loaded.pipelineBehavior?.autoBlock ?? true);
      setMinQuestionsPerSkill(loaded.policy?.minQuestionsPerSkill ?? 5);
      setMaxQuestionsPerSkill(loaded.policy?.maxQuestionsPerSkill ?? 50);
      setRequireHints(loaded.policy?.requireHints ?? true);
      setRequireExplanation(loaded.policy?.requireExplanation ?? true);
      setRequireVisuals(loaded.policy?.requireVisuals ?? false);
      setDistributionCheck(loaded.policy?.distributionCheck ?? true);
    });
  }, []);
  const handleSave = async () => {
    const newConfig: GovernanceConfigOverrides = {
      version: 1,
      qualityGates: {
        minCogLevel,
        maxCogLevel,
        minDidacticDepth,
        minQualityScore
      },
      pipelineStages: {
        schemaEnabled,
        typeEnabled,
        contentEnabled,
        supportEnabled,
        visualEnabled,
        graphEnabled
      },
      pipelineBehavior: {
        strictMode,
        autoBlock
      },
      policy: {
        minQuestionsPerSkill,
        maxQuestionsPerSkill,
        requireHints,
        requireExplanation,
        requireVisuals,
        distributionCheck
      },
      notifications: {
        notifyBlockers,
        notifyWarnings,
        notifyWeeklyReport,
        notifyNewRules
      },
      system: {
        cacheEnabled,
        debugMode,
        autoValidateOnSave,
        batchSize
      }
    };
    const success = await saveGovernanceConfig(newConfig);
    if (success) {
      setConfig(newConfig);
      setSaved(true);
      setHasChanges(false);
      setTimeout(() => setSaved(false), 3000);
    }
  };
  const handleReset = async () => {
    const defaults = await resetGovernanceConfig();
    setConfig(defaults);
    setMinCogLevel(defaults.qualityGates?.minCogLevel ?? 2);
    setMaxCogLevel(defaults.qualityGates?.maxCogLevel ?? 5);
    setMinDidacticDepth(defaults.qualityGates?.minDidacticDepth ?? 2);
    setMinQualityScore(defaults.qualityGates?.minQualityScore ?? 70);
    setSchemaEnabled(defaults.pipelineStages?.schemaEnabled ?? true);
    setTypeEnabled(defaults.pipelineStages?.typeEnabled ?? true);
    setContentEnabled(defaults.pipelineStages?.contentEnabled ?? true);
    setSupportEnabled(defaults.pipelineStages?.supportEnabled ?? true);
    setVisualEnabled(defaults.pipelineStages?.visualEnabled ?? true);
    setGraphEnabled(defaults.pipelineStages?.graphEnabled ?? true);
    setStrictMode(defaults.pipelineBehavior?.strictMode ?? false);
    setAutoBlock(defaults.pipelineBehavior?.autoBlock ?? true);
    setMinQuestionsPerSkill(defaults.policy?.minQuestionsPerSkill ?? 5);
    setMaxQuestionsPerSkill(defaults.policy?.maxQuestionsPerSkill ?? 50);
    setRequireHints(defaults.policy?.requireHints ?? true);
    setRequireExplanation(defaults.policy?.requireExplanation ?? true);
    setRequireVisuals(defaults.policy?.requireVisuals ?? false);
    setDistributionCheck(defaults.policy?.distributionCheck ?? true);
    setHasChanges(false);
  };
  const sections: {
    id: SectionId;
    label: string;
    icon: React.ReactNode;
  }[] = [{
    id: 'kwaliteit',
    label: 'Kwaliteitspoorten',
    icon: <ShieldIcon className="h-4 w-4" data-id="element-3269" />
  }, {
    id: 'pipeline',
    label: 'Pipeline',
    icon: <SlidersIcon className="h-4 w-4" data-id="element-3270" />
  }, {
    id: 'beleid',
    label: 'Beleid',
    icon: <CheckCircleIcon className="h-4 w-4" data-id="element-3271" />
  }, {
    id: 'meldingen',
    label: 'Meldingen',
    icon: <BellIcon className="h-4 w-4" data-id="element-3272" />
  }, {
    id: 'systeem',
    label: 'Systeem',
    icon: <DatabaseIcon className="h-4 w-4" data-id="element-3273" />
  }];
  const cogLevelNames: Record<number, string> = {
    1: 'Onthouden',
    2: 'Begrijpen',
    3: 'Toepassen',
    4: 'Analyseren',
    5: 'Evalueren',
    6: 'Creëren'
  };
  const didacticNames: Record<number, string> = {
    1: 'Oppervlakkig',
    2: 'Procedureel',
    3: 'Conceptueel',
    4: 'Analytisch',
    5: 'Creatief'
  };
  return <div className="space-y-5" data-id="element-3274">
      <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3" data-id="element-3275">
        <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-3276" />
        <div data-id="element-3277">
          <p className="text-sm font-semibold text-emerald-800" data-id="element-3278">
            Instellingen worden lokaal opgeslagen
          </p>
          <p className="text-xs text-emerald-700 mt-0.5 leading-relaxed" data-id="element-3279">
            Wijzigingen worden opgeslagen in je browser en blijven behouden na
            het herladen van de pagina.
            {config.lastSaved && <span className="ml-1 text-emerald-600" data-id="element-3280">
                Laatst opgeslagen:{' '}
                {new Date(config.lastSaved).toLocaleString('nl-NL')}
              </span>}
          </p>
        </div>
      </div>
      {(hasChanges || saved) && <div className={`flex items-center justify-between rounded-xl border px-4 py-3 ${saved ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`} data-id="element-3281">
          <div className="flex items-center gap-2" data-id="element-3282">
            {saved ? <CheckCircleIcon className="h-4 w-4 text-emerald-600" data-id="element-3283" /> : <AlertTriangleIcon className="h-4 w-4 text-amber-600" data-id="element-3284" />}
            <p className={`text-sm font-medium ${saved ? 'text-emerald-700' : 'text-amber-700'}`} data-id="element-3285">
              {saved ? 'Instellingen opgeslagen!' : 'Je hebt niet-opgeslagen wijzigingen'}
            </p>
          </div>
          {!saved && <div className="flex gap-2" data-id="element-3286">
              <Button variant="ghost" size="sm" onClick={handleReset} data-id="element-3287">
                <RotateCcwIcon className="h-3.5 w-3.5 mr-1" data-id="element-3288" />
                Herstel
              </Button>
              <Button variant="primary" size="sm" onClick={handleSave} data-id="element-3289">
                <SaveIcon className="h-3.5 w-3.5 mr-1" data-id="element-3290" />
                Opslaan
              </Button>
            </div>}
        </div>}
      <div className="flex flex-col gap-5 lg:flex-row" data-id="element-3291">
        <div className="lg:w-48 flex-shrink-0" data-id="element-3292">
          <nav className="space-y-1" data-id="element-3293">
            {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${activeSection === section.id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'}`} data-id="element-3294">
                <span className={activeSection === section.id ? 'text-blue-600' : 'text-gray-400'} data-id="element-3295">
                  {section.icon}
                </span>
                {section.label}
              </button>)}
          </nav>
        </div>
        <div className="flex-1 min-w-0" data-id="element-3296">
          <div className="rounded-xl border border-gray-100 bg-white p-5" data-id="element-3297">
            {activeSection === 'kwaliteit' && <div data-id="element-3298">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3299">
                  Kwaliteitspoorten
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3300">
                  Stel de minimale kwaliteitseisen in waaraan vragen moeten
                  voldoen.
                </p>
                <SliderField label="Minimaal cognitief niveau" description={`Vragen onder niveau ${minCogLevel} (${cogLevelNames[minCogLevel]}) worden gemarkeerd`} value={minCogLevel} min={1} max={6} onChange={v => {
              setMinCogLevel(v);
              markChanged();
            }} colorFn={v => v <= 2 ? 'text-amber-600' : 'text-blue-600'} data-id="element-3301" />
                <SliderField label="Maximaal cognitief niveau" description={`Vragen boven niveau ${maxCogLevel} (${cogLevelNames[maxCogLevel]}) worden gemarkeerd`} value={maxCogLevel} min={1} max={6} onChange={v => {
              setMaxCogLevel(v);
              markChanged();
            }} colorFn={v => v >= 5 ? 'text-emerald-600' : 'text-blue-600'} data-id="element-3302" />
                <SliderField label="Minimale didactische diepte" description={`Vragen onder diepte ${minDidacticDepth} (${didacticNames[minDidacticDepth]}) worden gemarkeerd`} value={minDidacticDepth} min={1} max={5} onChange={v => {
              setMinDidacticDepth(v);
              markChanged();
            }} data-id="element-3303" />
                <SliderField label="Minimale kwaliteitsscore" description="Vragen onder deze drempel worden als onvoldoende beschouwd" value={minQualityScore} min={0} max={100} step={5} onChange={v => {
              setMinQualityScore(v);
              markChanged();
            }} unit="%" colorFn={v => v >= 80 ? 'text-emerald-600' : v >= 60 ? 'text-amber-600' : 'text-red-600'} data-id="element-3304" />
              </div>}
            {activeSection === 'pipeline' && <div data-id="element-3305">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3306">
                  Pipeline Instellingen
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3307">
                  Schakel validatiefasen in of uit en configureer het
                  pipeline-gedrag.
                </p>
                <Toggle label="Schema Validatie" description="Controleer JSON-structuur en verplichte velden" value={schemaEnabled} onChange={v => {
              setSchemaEnabled(v);
              markChanged();
            }} data-id="element-3308" />
                <Toggle label="Vraagtype Validatie" description="Controleer vraagtype-specifieke regels" value={typeEnabled} onChange={v => {
              setTypeEnabled(v);
              markChanged();
            }} data-id="element-3309" />
                <Toggle label="Inhoud & Taalvalidatie" description="Controleer didactische kwaliteit en taalgebruik" value={contentEnabled} onChange={v => {
              setContentEnabled(v);
              markChanged();
            }} data-id="element-3310" />
                <Toggle label="Hint & Uitleg Validatie" description="Controleer aanwezigheid van ondersteunend materiaal" value={supportEnabled} onChange={v => {
              setSupportEnabled(v);
              markChanged();
            }} data-id="element-3311" />
                <Toggle label="Visuele Hulpmiddelen Validatie" description="Controleer visuele componenten en antwoordlekken" value={visualEnabled} onChange={v => {
              setVisualEnabled(v);
              markChanged();
            }} data-id="element-3312" />
                <Toggle label="LDG-Graaf Validatie" description="Controleer leerpad-afhankelijkheden" value={graphEnabled} onChange={v => {
              setGraphEnabled(v);
              markChanged();
            }} data-id="element-3313" />
                <div className="mt-4 pt-4 border-t border-gray-100" data-id="element-3314">
                  <Toggle label="Strikte modus" description="Behandel waarschuwingen als blokkerende fouten" value={strictMode} onChange={v => {
                setStrictMode(v);
                markChanged();
              }} data-id="element-3315" />
                  <Toggle label="Automatisch blokkeren" description="Blokkeer vragen automatisch bij BLOCKER-fouten" value={autoBlock} onChange={v => {
                setAutoBlock(v);
                markChanged();
              }} data-id="element-3316" />
                </div>
              </div>}
            {activeSection === 'beleid' && <div data-id="element-3317">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3318">
                  Beleidsregels
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3319">
                  Configureer de beleidsregels voor vraagsets en
                  vaardigheidsdekking.
                </p>
                <NumberField label="Minimaal aantal vragen per vaardigheid" description="Vaardigheden met minder vragen krijgen een waarschuwing" value={minQuestionsPerSkill} min={1} max={20} onChange={v => {
              setMinQuestionsPerSkill(v);
              markChanged();
            }} unit="vragen" data-id="element-3320" />
                <NumberField label="Maximaal aantal vragen per vaardigheid" description="Vaardigheden met meer vragen krijgen een informatieve melding" value={maxQuestionsPerSkill} min={10} max={200} onChange={v => {
              setMaxQuestionsPerSkill(v);
              markChanged();
            }} unit="vragen" data-id="element-3321" />
                <Toggle label="Hints verplicht" description="Vragen zonder hint krijgen een waarschuwing" value={requireHints} onChange={v => {
              setRequireHints(v);
              markChanged();
            }} data-id="element-3322" />
                <Toggle label="Uitleg verplicht" description="Vragen zonder uitleg krijgen een waarschuwing" value={requireExplanation} onChange={v => {
              setRequireExplanation(v);
              markChanged();
            }} data-id="element-3323" />
                <Toggle label="Visuele hulpmiddelen verplicht" description="Vragen zonder visueel hulpmiddel krijgen een waarschuwing" value={requireVisuals} onChange={v => {
              setRequireVisuals(v);
              markChanged();
            }} data-id="element-3324" />
                <Toggle label="Cognitief niveau distributie controleren" description="Controleer of vaardigheden een gevarieerde verdeling hebben" value={distributionCheck} onChange={v => {
              setDistributionCheck(v);
              markChanged();
            }} data-id="element-3325" />
              </div>}
            {activeSection === 'meldingen' && <div data-id="element-3326">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3327">
                  Meldingsinstellingen
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3328">
                  Kies wanneer je meldingen wilt ontvangen over
                  governance-activiteit.
                </p>
                <Toggle label="Meldingen bij blokkerende fouten" description="Ontvang een melding wanneer een vraag wordt geblokkeerd" value={notifyBlockers} onChange={v => {
              setNotifyBlockers(v);
              markChanged();
            }} data-id="element-3329" />
                <Toggle label="Meldingen bij waarschuwingen" description="Ontvang een melding bij elke nieuwe waarschuwing" value={notifyWarnings} onChange={v => {
              setNotifyWarnings(v);
              markChanged();
            }} data-id="element-3330" />
                <Toggle label="Wekelijks kwaliteitsrapport" description="Ontvang elke week een samenvatting van de kwaliteitsmetingen" value={notifyWeeklyReport} onChange={v => {
              setNotifyWeeklyReport(v);
              markChanged();
            }} data-id="element-3331" />
                <Toggle label="Meldingen bij nieuwe regels" description="Ontvang een melding wanneer nieuwe governance-regels worden toegevoegd" value={notifyNewRules} onChange={v => {
              setNotifyNewRules(v);
              markChanged();
            }} data-id="element-3332" />
              </div>}
            {activeSection === 'systeem' && <div data-id="element-3333">
                <h2 className="text-base font-bold text-gray-900 mb-1" data-id="element-3334">
                  Systeeminstellingen
                </h2>
                <p className="text-sm text-gray-500 mb-5" data-id="element-3335">
                  Technische instellingen voor het governance-systeem.
                </p>
                <Toggle label="Cache inschakelen" description="Sla validatieresultaten op in cache voor snellere verwerking" value={cacheEnabled} onChange={v => {
              setCacheEnabled(v);
              markChanged();
            }} data-id="element-3336" />
                <Toggle label="Automatisch valideren bij opslaan" description="Voer validatie automatisch uit wanneer een vraag wordt opgeslagen" value={autoValidateOnSave} onChange={v => {
              setAutoValidateOnSave(v);
              markChanged();
            }} data-id="element-3337" />
                <Toggle label="Foutopsporingsmodus" description="Toon uitgebreide foutinformatie in de console" value={debugMode} onChange={v => {
              setDebugMode(v);
              markChanged();
            }} data-id="element-3338" />
                <NumberField label="Batch-grootte voor validatie" description="Aantal vragen dat tegelijk wordt gevalideerd" value={batchSize} min={10} max={500} onChange={v => {
              setBatchSize(v);
              markChanged();
            }} unit="vragen" data-id="element-3339" />
                <div className="mt-6 pt-4 border-t border-gray-100" data-id="element-3340">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3" data-id="element-3341">
                    Gevaarlijke Acties
                  </h3>
                  <div className="space-y-2" data-id="element-3342">
                    <Button variant="secondary" size="sm" className="w-full justify-start" data-id="element-3343">
                      <RotateCcwIcon className="h-4 w-4 mr-2" data-id="element-3344" />
                      Cache leegmaken
                    </Button>
                    <Button variant="danger" size="sm" className="w-full justify-start" data-id="element-3345">
                      Alle instellingen terugzetten naar standaard
                    </Button>
                  </div>
                </div>
              </div>}
          </div>
          <div className="flex justify-end gap-3 mt-4" data-id="element-3346">
            <Button variant="ghost" size="sm" onClick={handleReset} disabled={!hasChanges} data-id="element-3347">
              <RotateCcwIcon className="h-4 w-4 mr-1.5" data-id="element-3348" />
              Herstel
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={!hasChanges} data-id="element-3349">
              <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-3350" />
              Wijzigingen Opslaan
            </Button>
          </div>
        </div>
      </div>
    </div>;
}